import { motion, useAnimation } from "motion/react";
import { Shield, AlertTriangle, CheckCircle, Zap, Target, TrendingDown } from "lucide-react";
import { useState, useEffect } from "react";

export function BotKillerSection() {
  const [detectedBots, setDetectedBots] = useState(0);
  const [blockedPercentage, setBlockedPercentage] = useState(0);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setDetectedBots((prev) => (prev < 997 ? prev + Math.floor(Math.random() * 20) : 997));
      setBlockedPercentage((prev) => (prev < 99.7 ? prev + 0.1 : 99.7));
    }, 100);

    const scanInterval = setInterval(() => {
      setScanning(true);
      setTimeout(() => setScanning(false), 1000);
    }, 3000);

    return () => {
      clearInterval(interval);
      clearInterval(scanInterval);
    };
  }, []);

  const botTypes = [
    {
      icon: AlertTriangle,
      name: "Scraper Bots",
      description: "Bots que roubam conteúdo e preços dos concorrentes",
      threat: "ALTO",
      blocked: "99.8%",
      color: "#FF4D00"
    },
    {
      icon: Target,
      name: "Click Fraud Bots",
      description: "Bots que inflam custos de anúncios com cliques falsos",
      threat: "CRÍTICO",
      blocked: "99.9%",
      color: "#FF4D00"
    },
    {
      icon: TrendingDown,
      name: "Spam Crawlers",
      description: "Tráfego lixo que distorce suas métricas analíticas",
      threat: "MÉDIO",
      blocked: "99.5%",
      color: "#FF4D00"
    }
  ];

  const detectionMethods = [
    "Behavioral Pattern Analysis",
    "IP Reputation Database",
    "User-Agent Fingerprinting",
    "JavaScript Challenge Response",
    "Mouse Movement Detection",
    "Request Rate Limiting"
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/95 to-[#0B0E14]" />

      {/* Warning stripes */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #FF4D00 0px,
              #FF4D00 20px,
              transparent 20px,
              transparent 40px
            )`
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF4D00]/30 bg-[#FF4D00]/5 mb-6">
            <Shield className="w-4 h-4 text-[#FF4D00]" />
            <span className="text-sm text-[#FF4D00] font-mono">BOT DETECTION SYSTEM</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Bot Killer System
            <span className="block text-[#FF4D00]">99.7% Precisão</span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Seus dados estão sendo <span className="text-[#FF4D00] font-bold">envenenados</span> por tráfego falso. 
            Eliminamos bots, scrapers e crawlers antes que distorçam suas decisões.
          </p>
        </motion.div>

        {/* Main Shield Visual */}
        <div className="max-w-5xl mx-auto mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Shield Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative w-full max-w-[400px] mx-auto aspect-square">
                {/* Shield base */}
                <motion.div
                  animate={{
                    boxShadow: scanning
                      ? '0 0 60px rgba(255,77,0,0.6)'
                      : '0 0 30px rgba(255,77,0,0.3)'
                  }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    {/* Shield shape */}
                    <motion.path
                      d="M100 10 L170 40 L170 90 Q170 150 100 190 Q30 150 30 90 L30 40 Z"
                      fill="url(#shieldGradient)"
                      stroke="#FF4D00"
                      strokeWidth="2"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 2 }}
                    />
                    <defs>
                      <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#FF4D00', stopOpacity: 0.3 }} />
                        <stop offset="100%" style={{ stopColor: '#0B0E14', stopOpacity: 0.8 }} />
                      </linearGradient>
                    </defs>

                    {/* Center icon */}
                    <g transform="translate(100, 100)">
                      <motion.circle
                        r="25"
                        fill="rgba(255,77,0,0.2)"
                        stroke="#FF4D00"
                        strokeWidth="2"
                        animate={{ scale: scanning ? [1, 1.2, 1] : 1 }}
                        transition={{ duration: 0.5 }}
                      />
                      <motion.path
                        d="M-10 0 L-5 8 L10 -8"
                        stroke="#FF4D00"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: scanning ? 1 : 0 }}
                        transition={{ duration: 0.5 }}
                      />
                    </g>
                  </svg>
                </motion.div>

                {/* Scanning wave effect */}
                {scanning && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 1 }}
                    animate={{ scale: 2, opacity: 0 }}
                    transition={{ duration: 1 }}
                    className="absolute inset-0 rounded-full border-4 border-[#FF4D00]"
                    style={{ borderRadius: '50%' }}
                  />
                )}

                {/* Orbiting particles */}
                {[0, 1, 2, 3, 4, 5].map((idx) => {
                  const angle = (idx / 6) * 360;
                  return (
                    <motion.div
                      key={idx}
                      className="absolute w-3 h-3 rounded-full bg-[#FF4D00]"
                      style={{
                        left: '50%',
                        top: '50%'
                      }}
                      animate={{
                        x: [
                          Math.cos((angle * Math.PI) / 180) * 150,
                          Math.cos(((angle + 360) * Math.PI) / 180) * 150
                        ],
                        y: [
                          Math.sin((angle * Math.PI) / 180) * 150,
                          Math.sin(((angle + 360) * Math.PI) / 180) * 150
                        ]
                      }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: 'linear',
                        delay: idx * 0.3
                      }}
                    />
                  );
                })}
              </div>

              {/* Stats below shield */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-8 grid grid-cols-2 gap-4"
              >
                <div className="p-4 rounded-lg border border-[#FF4D00]/30 bg-[#FF4D00]/5 text-center">
                  <div className="text-3xl font-bold text-[#FF4D00] font-mono">
                    {detectedBots}
                  </div>
                  <div className="text-xs text-gray-400">Bots Bloqueados Hoje</div>
                </div>
                <div className="p-4 rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 text-center">
                  <div className="text-3xl font-bold text-[#10B981] font-mono">
                    {blockedPercentage.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-400">Taxa de Detecção</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Bot Types */}
            <div className="space-y-6">
              {botTypes.map((bot, idx) => {
                const Icon = bot.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                    whileHover={{ x: 10, scale: 1.02 }}
                    className="relative group"
                  >
                    {/* Glow */}
                    <div className="absolute -inset-0.5 rounded-xl bg-[#FF4D00] opacity-0 group-hover:opacity-20 blur-xl transition-opacity" />

                    {/* Card */}
                    <div className="relative p-6 rounded-xl border border-[#FF4D00]/30 bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/50 backdrop-blur-sm">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#FF4D00]/10 border-2 border-[#FF4D00] flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-[#FF4D00]" />
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-white">{bot.name}</h3>
                            <span className="px-2 py-1 rounded text-xs font-bold bg-[#FF4D00]/20 text-[#FF4D00] border border-[#FF4D00]/40">
                              {bot.threat}
                            </span>
                          </div>
                          <p className="text-sm text-gray-300 mb-3">{bot.description}</p>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-[#10B981]" />
                            <span className="text-sm text-[#10B981] font-mono">{bot.blocked} bloqueados</span>
                          </div>
                        </div>
                      </div>

                      {/* Scan line */}
                      <motion.div
                        className="absolute inset-0 pointer-events-none rounded-xl overflow-hidden"
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                      >
                        <motion.div
                          className="absolute inset-x-0 h-px bg-[#FF4D00] opacity-50"
                          animate={{ y: [0, 200] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                        />
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detection Methods */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              6 Camadas de Detecção
            </h3>
            <p className="text-gray-300">
              Machine learning + análise comportamental = proteção inquebrável
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {detectionMethods.map((method, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                whileHover={{ y: -5, scale: 1.05 }}
                className="p-6 rounded-xl border border-[#00F5FF]/30 bg-gradient-to-br from-[#00F5FF]/5 to-transparent backdrop-blur-sm text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-[#00F5FF]/10 border-2 border-[#00F5FF] flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#00F5FF]" />
                </div>
                <div className="text-white font-semibold">{method}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Impact Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto p-8 rounded-2xl border-2 border-[#FF4D00]/30 bg-gradient-to-br from-[#FF4D00]/10 to-transparent backdrop-blur-sm"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Impacto Real nas Lojas</h3>
            <p className="text-gray-300">Resultados medidos após ativação do Bot Killer</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { label: 'Redução de Tráfego Falso', value: '-73%', color: '#10B981' },
              { label: 'Economia em Anúncios', value: 'R$ 12k/mês', color: '#10B981' },
              { label: 'Melhora na Precisão', value: '+89%', color: '#10B981' },
              { label: 'Bots Bloqueados', value: '1.2M+', color: '#FF4D00' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div
                  className="text-3xl font-bold font-mono mb-1"
                  style={{ color: stat.color }}
                >
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-300 mb-8">
            Decisões baseadas em <span className="text-[#FF4D00] font-bold">dados limpos</span> ou 
            em <span className="text-gray-500 line-through">dados envenenados</span>?
          </p>
          <button className="px-10 py-5 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white text-lg rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
            Ativar Proteção Agora
          </button>
        </motion.div>
      </div>
    </section>
  );
}
